import { useCallback, useEffect, useRef, useState } from 'react';
import { useNotificaciones } from '../../contextos/notificationContext';
import { useConfirmacion } from '../../contextos/confirmacionContext';
import { EnumTipoNotificacion } from '../../interfaces';

/** Id temporal para filas nuevas (el back asigna el definitivo). */
export const generarIdTemporal = () => Math.random().toString(36).substring(2, 9);

/**
 * Estado de edición de una tabla editable (meta de la tabla).
 * Los cambios se escriben en la fila mientras se edita y se envían al back al guardar la fila completa.
 */
export interface IEdicionFilasMeta<T> {
    filaEnEdicion: string | null;
    intentoGuardar: boolean;
    actualizarFila: (id: string, campo: keyof T, valor: number | string) => void;
    iniciarEdicion: (id: string) => void;
    guardarFila: (id: string) => void;
    cancelarEdicion: () => void;
    /** Si no se envía, la tabla no elimina filas. */
    eliminarFila?: (id: string) => void;
    /** Solo lectura: oculta las acciones (p. ej. cotización aceptada). */
    soloLectura?: boolean;
}

/** Coordinación entre tablas para que solo haya una fila en edición en toda la pantalla. */
export interface ICoordinacionEdicionProps {
    onRegistrarCancelacion?: (clave: string, cancelar: () => void) => void;
    onIniciarEdicionGeneral?: () => void;
}

/** Lo que una tabla editable entrega a su vista. */
export interface ITablaEditable<T> {
    datos: T[];
    meta: IEdicionFilasMeta<T>;
    /** Sin ella la tabla no agrega filas. */
    agregarFila?: () => void;
}

interface OpcionesEdicionFilas<T extends { Id: string }> extends ICoordinacionEdicionProps {
    /** Identifica la tabla ante la coordinación de ediciones. */
    clave: string;
    datosIniciales: T[];
    camposObligatorios: (keyof T)[];
    /** Fila vacía para "Agregar fila". Sin ella la tabla no agrega filas. */
    crearFila?: () => T;
    permiteEliminar?: boolean;
    soloLectura?: boolean;
    /** TODO back: cada pantalla envía aquí la fila guardada (crear o actualizar). */
    alGuardarFila?: (fila: T, esNueva: boolean) => void;
    alEliminarFila?: (id: string) => void;
}

const estaVacio = (valor: unknown) => valor === null || valor === undefined || String(valor).trim() === '';

/**
 * Edición en línea de una tabla, como en las matrices NBS: una fila a la vez, los cambios se escriben
 * en la fila mientras se edita y van al back solo al guardarla completa. Cancelar restaura la fila.
 */
export const useEdicionFilas = <T extends { Id: string }>({
    clave,
    datosIniciales,
    camposObligatorios,
    crearFila,
    permiteEliminar = false,
    soloLectura = false,
    alGuardarFila,
    alEliminarFila,
    onRegistrarCancelacion,
    onIniciarEdicionGeneral
}: OpcionesEdicionFilas<T>): ITablaEditable<T> => {
    const { mostrarNotificacion } = useNotificaciones();
    const { confirmar } = useConfirmacion();

    const [datos, setDatos] = useState<T[]>(datosIniciales);
    const [filaEnEdicion, setFilaEnEdicion] = useState<string | null>(null);
    const [intentoGuardar, setIntentoGuardar] = useState(false);

    // Refs: la cancelación se registra una vez y debe ver siempre la fila actual.
    const filaEnEdicionRef = useRef<string | null>(null);
    const respaldoRef = useRef<T | null>(null);
    const esNuevaRef = useRef(false);

    const empezarEdicion = (id: string, respaldo: T | null, esNueva: boolean) => {
        filaEnEdicionRef.current = id;
        respaldoRef.current = respaldo;
        esNuevaRef.current = esNueva;
        setIntentoGuardar(false);
        setFilaEnEdicion(id);
    };

    const terminarEdicion = () => {
        filaEnEdicionRef.current = null;
        respaldoRef.current = null;
        esNuevaRef.current = false;
        setIntentoGuardar(false);
        setFilaEnEdicion(null);
    };

    const cancelarEdicion = useCallback(() => {
        const id = filaEnEdicionRef.current;
        if (!id) return;
        const respaldo = respaldoRef.current;
        setDatos(prev => esNuevaRef.current
            ? prev.filter(f => f.Id !== id)
            : prev.map(f => f.Id === id && respaldo ? respaldo : f));
        terminarEdicion();
    }, []);

    useEffect(() => {
        onRegistrarCancelacion?.(clave, cancelarEdicion);
    }, [clave, onRegistrarCancelacion, cancelarEdicion]);

    const actualizarFila = (id: string, campo: keyof T, valor: number | string) => {
        setDatos(prev => prev.map(f => f.Id === id ? { ...f, [campo]: valor } : f));
    };

    const iniciarEdicion = (id: string) => {
        onIniciarEdicionGeneral?.();
        empezarEdicion(id, datos.find(f => f.Id === id) ?? null, false);
    };

    const agregarFila = () => {
        if (!crearFila) return;
        onIniciarEdicionGeneral?.();
        const nueva = crearFila();
        // La fila nueva va de primera para que quede a la vista.
        setDatos(prev => [nueva, ...prev]);
        empezarEdicion(nueva.Id, null, true);
    };

    const guardarFila = (id: string) => {
        const fila = datos.find(f => f.Id === id);
        if (!fila || camposObligatorios.some(campo => estaVacio(fila[campo]))) {
            setIntentoGuardar(true);
            mostrarNotificacion(EnumTipoNotificacion.Warning, 'Campos Obligatorios', 'Debes diligenciar todos los campos obligatorios antes de guardar la fila.');
            return;
        }
        alGuardarFila?.(fila, esNuevaRef.current);
        terminarEdicion();
        mostrarNotificacion(EnumTipoNotificacion.Success, 'Fila Guardada', 'La fila se guardó correctamente.', 2500);
    };

    const eliminarFila = async (id: string) => {
        const confirmado = await confirmar({
            titulo: 'Eliminar fila',
            mensaje: '¿Está seguro de que desea eliminar esta fila? Esta acción no se puede deshacer.',
            textoAceptar: 'Sí, eliminar',
            tipo: EnumTipoNotificacion.Warning,
            opcionCancelar: true
        });
        if (!confirmado) return;
        alEliminarFila?.(id);
        setDatos(prev => prev.filter(f => f.Id !== id));
        mostrarNotificacion(EnumTipoNotificacion.Success, 'Fila Eliminada', 'La fila se eliminó correctamente.', 2500);
    };

    return {
        datos,
        meta: {
            filaEnEdicion,
            intentoGuardar,
            actualizarFila,
            iniciarEdicion,
            guardarFila,
            cancelarEdicion,
            eliminarFila: permiteEliminar && !soloLectura ? eliminarFila : undefined,
            soloLectura
        },
        agregarFila: crearFila && !soloLectura ? agregarFila : undefined
    };
};
